import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Hash, Smartphone, Monitor, Radio, Search } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { StatCard } from './StatCard';
import { formatDateBR } from '../lib/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ORIGEM_COR = {
  self_service: 'text-blue-300 bg-blue-500/15 border-blue-500/40',
  painel: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/40',
  operadora: 'text-amber-300 bg-amber-500/15 border-amber-500/40',
};
const mesAtual = () => new Date().toISOString().slice(0, 7);

function exportarCsv(itens, mes) {
  const cab = ['Data', 'Origem', 'Cliente', 'CPF', 'Numero', 'ICCID', 'Operadora', 'Oferta', 'Plano', 'Valor', 'Status'];
  const linhas = itens.map(i => [formatDateBR(i.data), i.origem_label, i.cliente_nome, i.cpf, i.numero, `'${i.iccid}`, i.operadora,
    i.oferta_nome, i.plano_nome, String(i.valor).replace('.', ','), i.status]);
  const csv = [cab, ...linhas].map(l => l.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';')).join('\n');
  const url = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = `ativacoes_${mes || 'todas'}.csv`; a.click();
  URL.revokeObjectURL(url);
}

export function TodasAtivacoes() {
  const [dados, setDados] = useState(null);
  const [mes, setMes] = useState(mesAtual());
  const [origem, setOrigem] = useState('');
  const [busca, setBusca] = useState('');

  useEffect(() => {
    const p = new URLSearchParams();
    if (mes) p.set('mes', mes);
    if (origem) p.set('origem', origem);
    axios.get(`${API_URL}/api/relatorio/ativacoes?${p}`, { withCredentials: true })
      .then(r => setDados(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Erro ao carregar ativações'));
  }, [mes, origem]);

  const itens = useMemo(() => {
    const s = busca.trim().toLowerCase();
    const lista = dados?.itens || [];
    return s ? lista.filter(i => [i.cliente_nome, i.cpf, i.numero, i.iccid].some(v => (v || '').toLowerCase().includes(s))) : lista;
  }, [dados, busca]);

  const por = dados?.resumo?.por_origem || {};
  const meses = Array.from(new Set([mesAtual(), ...(dados?.meses || [])]));

  return (
    <div className="space-y-4" data-testid="todas-ativacoes">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Hash} label="Total" value={dados?.resumo?.total ?? '—'} color="blue" testId="ativ-total" />
        <StatCard icon={Smartphone} label="Self-service" value={por.self_service ?? 0} color="blue" testId="ativ-self-service" />
        <StatCard icon={Monitor} label="Painel" value={por.painel ?? 0} color="emerald" testId="ativ-painel" />
        <StatCard icon={Radio} label="Plataforma da operadora" value={por.operadora ?? 0} color="amber" testId="ativ-operadora" />
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <select value={mes} onChange={e => setMes(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm" data-testid="ativ-filtro-mes">
          <option value="">Todos os meses</option>
          {meses.map(m => <option key={m} value={m}>{m.split('-').reverse().join('/')}</option>)}
        </select>
        <select value={origem} onChange={e => setOrigem(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm" data-testid="ativ-filtro-origem">
          <option value="">Todas as origens</option>
          {Object.entries(dados?.origens || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar cliente, CPF, número ou ICCID..." className="form-input pl-10" data-testid="ativ-busca" />
        </div>
        <Button variant="outline" className="btn-secondary" onClick={() => exportarCsv(itens, mes)} disabled={!itens.length} data-testid="ativ-exportar">
          <Download className="w-4 h-4 mr-2" /> Exportar planilha
        </Button>
      </div>
      <TabelaAtivacoes itens={itens} carregando={!dados} />
    </div>
  );
}

function TabelaAtivacoes({ itens, carregando }) {
  if (carregando) return <p className="text-sm text-zinc-400">Carregando...</p>;
  if (!itens.length) return <p className="text-sm text-zinc-400" data-testid="ativ-vazio">Nenhuma ativação neste período.</p>;
  return (
    <div className="table-container overflow-x-auto">
      <table className="data-table w-full text-sm" data-testid="ativ-tabela">
        <thead><tr>
          {['Data', 'Origem', 'Cliente', 'Número', 'Operadora', 'Oferta / Plano', 'Valor', 'Status'].map(h => <th key={h}>{h}</th>)}
        </tr></thead>
        <tbody>
          {itens.map(i => (
            <tr key={i.linha_id} data-testid={`ativ-linha-${i.linha_id}`}>
              <td className="whitespace-nowrap">{formatDateBR(i.data)}</td>
              <td><span className={`px-2 py-0.5 rounded border text-xs ${ORIGEM_COR[i.origem] || ''}`}>{i.origem_label}</span></td>
              <td>{i.cliente_nome}<div className="text-xs text-zinc-500 font-mono">{i.cpf}</div></td>
              <td className="font-mono">{i.numero}</td>
              <td>{i.operadora}</td>
              <td>{i.oferta_nome || <span className="text-zinc-500">sem oferta</span>}<div className="text-xs text-zinc-500">{i.plano_nome}</div></td>
              <td className="whitespace-nowrap">R$ {Number(i.valor || 0).toFixed(2).replace('.', ',')}</td>
              <td>{i.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
